import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Alert,
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  TrendingFlat as TrendFlatIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { ActionButton } from '../../components/common';
import { useAcao } from '../../authorization/useVeredito';
import psychosocialService from '../../services/psychosocialService';
import { PsychosocialDashboardData, PsychosocialAISuggestion } from '../../types/psychosocial';

const classificationColors: Record<string, string> = {
  green: '#4caf50',
  yellow: '#ff9800',
  red: '#f44336',
};

type DimensionClassification = PsychosocialDashboardData['classificationByDimension'][number];
type HeatmapDepartment = PsychosocialDashboardData['heatmap']['departments'][number];

const dashboardBreadcrumbs = (id: string | undefined) => [
  { label: 'Psicossocial', path: '/psychosocial' },
  { label: 'Campanha', path: `/psychosocial/campaigns/${id}` },
  { label: 'Dashboard' },
];

const PsychosocialCampaignDashboardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  // O relatório agregado é o que sobra para quem não alcança a resposta individual.
  const resultadoIndividual = useAcao('psychosocial_response.view_individual', 'Ver Resultados');
  const veResultadoIndividual = !resultadoIndividual.disabled;
  const podeGerarPlano = useAcao('action_plan.create', 'Sugerir ações (IA)');
  const podeSugerirIA = !podeGerarPlano.disabled;

  // Sugestão de plano de ação por IA (dados agregados apenas — LGPD).
  const [iaOpen, setIaOpen] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaGerando, setIaGerando] = useState(false);
  const [iaErro, setIaErro] = useState<string | null>(null);
  const [iaAcoes, setIaAcoes] = useState<PsychosocialAISuggestion[]>([]);
  const [iaModelo, setIaModelo] = useState<string>('');
  const [iaStatusId, setIaStatusId] = useState<number | null>(null);

  const ROTULO_PRIORIDADE: Record<string, string> = {
    IRRELEVANTE: 'Irrelevante', DE_ATENCAO: 'De atenção', CRITICA: 'Crítica', INTOLERAVEL: 'Intolerável',
  };
  const ROTULO_RESPONSAVEL: Record<string, string> = {
    EMPREGADOR: 'Empregador', PROFISSIONAL_SEGURANCA_HABILITADO: 'Prof. de Segurança',
    PROFISSIONAL_SST: 'Prof. de SST', MEDICO_DO_TRABALHO: 'Médico do Trabalho',
    PROFISSIONAL_HABILITADO: 'Prof. Habilitado',
  };
  const ROTULO_TIPO: Record<string, string> = {
    APRIMORAR: 'Aprimorar', INTRODUZIR: 'Introduzir', MANTER: 'Manter',
  };
  const corDaPrioridade = (p: string): 'error' | 'warning' | 'success' | 'default' =>
    p === 'INTOLERAVEL' || p === 'CRITICA' ? 'error' : p === 'DE_ATENCAO' ? 'warning' : 'success';

  const extrairErro = (e: unknown, padrao: string): string => {
    const err = e as { response?: { data?: { error?: string } }; message?: string };
    return err.response?.data?.error ?? err.message ?? padrao;
  };

  // Abrir o diálogo carrega as sugestões JÁ SALVAS desta campanha (não gera de novo).
  const handleAbrirIA = useCallback(async () => {
    if (!id) return;
    setIaOpen(true);
    setIaLoading(true);
    setIaErro(null);
    try {
      const salvas = await psychosocialService.listActionSuggestions(Number(id));
      setIaAcoes(salvas);
    } catch (e) {
      setIaErro(extrairErro(e, 'Falha ao carregar sugestões.'));
    } finally {
      setIaLoading(false);
    }
  }, [id]);

  // Gerar com a IA: substitui as ainda-não-avaliadas e salva; preserva aprovadas/descartadas.
  const handleGerarIA = useCallback(async () => {
    if (!id) return;
    setIaGerando(true);
    setIaErro(null);
    try {
      const res = await psychosocialService.suggestActionPlan(Number(id));
      setIaAcoes(res.acoes);
      setIaModelo(res.modelo);
    } catch (e) {
      setIaErro(extrairErro(e, 'Falha ao gerar sugestões.'));
    } finally {
      setIaGerando(false);
    }
  }, [id]);

  // Aprovar / descartar / reabrir uma sugestão — persiste no banco.
  const handleStatusIA = useCallback(async (suggestionId: number, status: 'aprovada' | 'descartada' | 'sugerida') => {
    if (!id) return;
    setIaStatusId(suggestionId);
    try {
      const atualizada = await psychosocialService.updateActionSuggestionStatus(Number(id), suggestionId, status);
      setIaAcoes((prev) => prev.map((a) => (a.id === suggestionId ? atualizada : a)));
    } catch (e) {
      enqueueSnackbar(extrairErro(e, 'Falha ao atualizar a sugestão.'), { variant: 'error' });
    } finally {
      setIaStatusId(null);
    }
  }, [id, enqueueSnackbar]);

  // Elo: transforma as sugestões APROVADAS em um Plano de Ação (NR-1) e leva para a tela dele.
  const [criandoPlano, setCriandoPlano] = useState(false);
  const handleCriarPlano = useCallback(async () => {
    if (!id) return;
    setCriandoPlano(true);
    try {
      const res = await psychosocialService.createActionPlanFromApproved(Number(id));
      enqueueSnackbar(`Plano de ação criado com ${res.itens} ação(ões).`, { variant: 'success' });
      setIaOpen(false);
      navigate(`/action-plan/${res.actionPlanId}`);
    } catch (e) {
      enqueueSnackbar(extrairErro(e, 'Falha ao criar o plano de ação.'), { variant: 'error' });
    } finally {
      setCriandoPlano(false);
    }
  }, [id, enqueueSnackbar, navigate]);

  const [dashboard, setDashboard] = useState<PsychosocialDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await psychosocialService.getDashboard(Number(id));
      setDashboard(data);
    } catch {
      enqueueSnackbar('Erro ao carregar dashboard', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleExport = async () => {
    try {
      const blob = await psychosocialService.exportExcel(Number(id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resultados-psicossocial-${id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('Erro ao exportar', { variant: 'error' });
    }
  };

  const heatmapColumns = useMemo<DataTableColumn<DimensionClassification>[]>(() => {
    const departments: HeatmapDepartment[] = dashboard?.heatmap.departments ?? [];
    return [
      {
        id: 'dimension',
        label: 'Dimensão',
        primary: true,
        render: (dim) => <Box component="span" sx={{ fontSize: 12 }}>{dim.dimensionName}</Box>,
      },
      ...departments.map((dept) => ({
        id: `dept-${dept.departmentId}`,
        label: dept.departmentName,
        align: 'center' as const,
        render: (dim: DimensionClassification) => {
          const val = dept.dimensions[dim.dimensionKey];
          if (!val) return '-';
          return (
            <Tooltip
              title={
                <Box sx={{ fontSize: 12 }}>
                  <div><b>{dim.dimensionName}</b> · {dept.departmentName}</div>
                  <div>Média setorial: {val.avgScore.toFixed(0)} (escala 0-100)</div>
                  <div>Respondentes (n): {val.n}</div>
                  <div>Desvio padrão: {val.desvioPadrao.toFixed(1)}</div>
                </Box>
              }
              arrow
            >
              <Chip
                size="small"
                label={val.avgScore.toFixed(0)}
                sx={{
                  bgcolor: classificationColors[val.classification],
                  color: 'white',
                  fontWeight: 'bold',
                  minWidth: 40,
                  cursor: 'help',
                }}
              />
            </Tooltip>
          );
        },
      })),
    ];
  }, [dashboard]);

  if (loading) {
    return (
      <PageShell
        breadcrumbs={dashboardBreadcrumbs(id)}
        title="Dashboard da Campanha"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      </PageShell>
    );
  }

  if (!dashboard) return null;

  const { status, classificationByDimension, riscoGeral, riskByDomain, heatmap, comparisonWithPrevious } = dashboard;

  return (
    <PageShell
      breadcrumbs={dashboardBreadcrumbs(id)}
      title="Dashboard da Campanha"
      subtitle="Visualize os resultados e métricas da campanha"
      actions={
        <>
          {podeSugerirIA && (
            <ActionButton
              acao="action_plan.create"
              rotulo="Sugerir ações (IA)"
              variant="contained"
              color="secondary"
              startIcon={<AIIcon />}
              onClick={() => void handleAbrirIA()}
            >
              Sugerir ações (IA)
            </ActionButton>
          )}
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}>
            {veResultadoIndividual ? 'Exportar Excel' : 'Exportar Relatório'}
          </Button>
          {veResultadoIndividual && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/psychosocial/campaigns/${id}/results`)}
            >
              Ver Resultados
            </Button>
          )}
        </>
      }
    >
      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: status.total, color: '#1976d2' },
          { label: 'Respondidos', value: status.completed, color: '#4caf50' },
          { label: 'Pendentes', value: status.pending + status.notified, color: '#ff9800' },
          { label: 'Expirados', value: status.expired, color: '#f44336' },
          { label: 'Taxa', value: `${status.completionRate}%`, color: '#9c27b0' },
        ].map((card) => (
          <Grid size={{ xs: 6, md: 'grow' }} key={card.label}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold' }}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Risco GERAL da empresa — headline que define a periodicidade da reavaliação (NR-1) */}
      {(() => {
        const nv = riscoGeral.nivel;
        const cor = nv === 'alto' ? classificationColors.red : nv === 'atencao' ? classificationColors.yellow : classificationColors.green;
        const rotulo = nv === 'alto' ? 'Risco Alto' : nv === 'atencao' ? 'Atenção' : 'Risco Baixo';
        const meses = riscoGeral.periodicidadeMeses;
        const periodicidade = meses % 12 === 0 ? `${meses / 12} ${meses === 12 ? 'ano' : 'anos'}` : `${meses} meses`;
        return (
          <Card sx={{ mb: 3, borderLeft: `10px solid ${cor}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Risco Geral da Empresa</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                    <Chip label={rotulo} sx={{ bgcolor: cor, color: 'white', fontWeight: 700, fontSize: '1rem', py: 2, px: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                      Classificação pelo pior setor/domínio (metodologia NR-1). Base agregada — sem resposta individual.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="overline" color="text.secondary">Reavaliação sugerida</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: cor }}>a cada {periodicidade}</Typography>
                  <Typography variant="caption" color="text.secondary">Ajustável pelo responsável técnico no PCMSO</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* Risco da empresa por domínio (grupos do COPSOQ) — resumo executivo NR-1 */}
      {riskByDomain.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Risco da Empresa por Domínio</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Os grandes grupos do COPSOQ. O domínio é Risco Alto quando 30% ou mais das respostas
              caem em risco alto (metodologia NR-1). Visão agregada — sem resposta individual.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {riskByDomain.map((d) => {
                const cor = d.nivel === 'alto' ? classificationColors.red : d.nivel === 'atencao' ? classificationColors.yellow : classificationColors.green;
                const rotulo = d.nivel === 'alto' ? 'Risco Alto' : d.nivel === 'atencao' ? 'Atenção' : 'Baixo';
                return (
                  <Box key={d.domain} sx={{ border: `2px solid ${cor}`, borderRadius: 2, p: 2, borderLeft: `8px solid ${cor}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, pr: 1 }}>{d.domain}</Typography>
                      <Chip size="small" label={rotulo} sx={{ bgcolor: cor, color: 'white', fontWeight: 700 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">{d.redPct}% das respostas em risco alto</Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Classification by Dimension */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Distribuição por Dimensão</Typography>
          {classificationByDimension.map((dim) => (
            <Box key={dim.dimensionKey} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{dim.dimensionName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {dim.green + dim.yellow + dim.red} respostas
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  height: 24,
                  borderRadius: 1,
                  overflow: 'hidden',
                  // Gradiente contínuo vermelho -> amarelo -> verde: cor pura no centro de cada
                  // faixa e transição suave nas bordas, mantendo as proporções por largura.
                  background: `linear-gradient(90deg, ${classificationColors.red} 0%, ${classificationColors.red} ${dim.redPct / 2}%, ${classificationColors.yellow} ${dim.redPct + dim.yellowPct / 2}%, ${classificationColors.green} ${dim.redPct + dim.yellowPct + dim.greenPct / 2}%, ${classificationColors.green} 100%)`,
                }}
              >
                {/* Ordem vermelho -> amarelo -> verde: o risco alto vem primeiro, à esquerda. */}
                {dim.redPct > 0 && (
                  <Tooltip title={`Vermelho: ${dim.redPct}%`}>
                    <Box sx={{ width: `${dim.redPct}%`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>{dim.redPct > 10 ? `${dim.redPct}%` : ''}</Typography>
                    </Box>
                  </Tooltip>
                )}
                {dim.yellowPct > 0 && (
                  <Tooltip title={`Amarelo: ${dim.yellowPct}%`}>
                    <Box sx={{ width: `${dim.yellowPct}%`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>{dim.yellowPct > 10 ? `${dim.yellowPct}%` : ''}</Typography>
                    </Box>
                  </Tooltip>
                )}
                {dim.greenPct > 0 && (
                  <Tooltip title={`Verde: ${dim.greenPct}%`}>
                    <Box sx={{ width: `${dim.greenPct}%`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>{dim.greenPct > 10 ? `${dim.greenPct}%` : ''}</Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: classificationColors.green, borderRadius: '50%' }} />
              <Typography variant="caption">Baixo risco</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: classificationColors.yellow, borderRadius: '50%' }} />
              <Typography variant="caption">Moderado</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: classificationColors.red, borderRadius: '50%' }} />
              <Typography variant="caption">Alto risco</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Nível de risco por setor — o que a empresa lê primeiro (sem expor resposta individual) */}
        {heatmap.departments.length > 0 && (
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Nível de Risco por Setor</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Classificação segundo a NR-1: o setor é Risco Alto quando a pior dimensão atinge 30%
                  ou mais dos trabalhadores em risco alto, ou quando uma dimensão crítica de saúde mental
                  ou sobrecarga dispara o gatilho de severidade. Baseado nas respostas agregadas — nenhuma
                  resposta individual é exibida.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {[...heatmap.departments]
                    .sort((a, b) => b.piorDimensaoPctVermelho - a.piorDimensaoPctVermelho)
                    .map((dept) => {
                      const nivel = dept.nivelGeral;
                      const cor = nivel === 'alto' ? classificationColors.red : nivel === 'atencao' ? classificationColors.yellow : classificationColors.green;
                      const rotulo = nivel === 'alto' ? 'Risco Alto' : nivel === 'atencao' ? 'Atenção' : 'Baixo';
                      return (
                        <Box key={dept.departmentId} sx={{ border: `2px solid ${cor}`, borderRadius: 2, p: 2, borderLeft: `8px solid ${cor}` }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{dept.departmentName}</Typography>
                            <Chip size="small" label={rotulo} sx={{ bgcolor: cor, color: 'white', fontWeight: 700 }} />
                          </Box>
                          {dept.piorDimensaoPctVermelho > 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              Pior fator: <b>{dept.piorDimensao}</b> — {dept.piorDimensaoPctVermelho}% dos trabalhadores em risco alto
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Nenhum trabalhador em risco alto</Typography>
                          )}
                          {dept.gatilhoSeveridade && (
                            <Typography variant="body2" sx={{ mt: 0.5, color: classificationColors.red, fontWeight: 600 }}>
                              ⚠ Gatilho de severidade: <b>{dept.gatilhoSeveridade}</b> exige ação imediata
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Heatmap */}
        {heatmap.departments.length > 0 && (
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6">Heatmap: Dimensões x Setores</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Cada célula é a <b>média setorial</b> da dimensão, na escala 0-100 (a cor segue o
                  risco real, não só o número). Passe o mouse para ver o nº de respondentes (n) e o
                  desvio padrão do setor.
                </Typography>
                <DataTable
                  columns={heatmapColumns}
                  rows={classificationByDimension}
                  getRowId={(dim) => dim.dimensionKey}
                  emptyState={{
                    title: 'Nenhuma dimensão avaliada nesta campanha',
                    description:
                      'O heatmap é calculado a partir das respostas que os funcionários enviam ao formulário desta campanha.',
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Comparison with previous */}
        {comparisonWithPrevious && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comparativo com "{comparisonWithPrevious.previousCampaignName}"
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Taxa de adesão: {comparisonWithPrevious.completionRateDelta > 0 ? '+' : ''}{comparisonWithPrevious.completionRateDelta}%
                </Typography>
                {comparisonWithPrevious.dimensionDeltas.slice(0, 8).map((delta) => (
                  <Box key={delta.dimensionKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {delta.trend === 'worsening' && <TrendUpIcon color="error" fontSize="small" />}
                    {delta.trend === 'improving' && <TrendDownIcon color="success" fontSize="small" />}
                    {delta.trend === 'stable' && <TrendFlatIcon color="action" fontSize="small" />}
                    <Typography variant="body2" sx={{ flex: 1 }}>{delta.dimensionName}</Typography>
                    <Typography
                      variant="body2"
                      color={delta.trend === 'worsening' ? 'error' : delta.trend === 'improving' ? 'success.main' : 'text.secondary'}
                    >
                      {delta.delta > 0 ? '+' : ''}{delta.delta}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Diálogo: sugestão de plano de ação por IA (só dados agregados — LGPD) */}
      <Dialog open={iaOpen} onClose={() => setIaOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon color="secondary" /> Plano de ação sugerido pela IA
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sugestões geradas a partir do <b>risco agregado</b> da campanha — nenhuma resposta
            individual é enviada à IA. Revise e ajuste antes de registrar no plano; a IA sugere,
            o responsável técnico decide.
          </Alert>
          {(iaLoading || iaGerando) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">
                {iaGerando ? 'Gerando sugestões com base na NR-1…' : 'Carregando sugestões salvas…'}
              </Typography>
            </Box>
          )}
          {iaErro && !iaLoading && !iaGerando && <Alert severity="error">{iaErro}</Alert>}
          {!iaLoading && !iaGerando && !iaErro && iaAcoes.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Nenhuma sugestão salva para esta campanha ainda.
              </Typography>
              <Button variant="contained" color="secondary" startIcon={<AIIcon />} onClick={handleGerarIA}>
                Gerar com IA
              </Button>
            </Box>
          )}
          {!iaLoading && !iaGerando && !iaErro && iaAcoes.length > 0 && (
            <Stack spacing={2}>
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Como ler as etiquetas de cada ação
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  <b>Prioridade</b> (colorida) define o prazo — Intolerável: imediato · Crítica: 3 meses ·
                  De atenção: 6 meses · Irrelevante: 24 meses.&nbsp;&nbsp;
                  <b>Tipo</b>: Introduzir (controle novo), Aprimorar (melhorar), Manter.&nbsp;&nbsp;
                  <b>Setor</b> alvo · <b>Dimensão</b> psicossocial atacada · <b>Norma</b> de referência.
                </Typography>
              </Box>
              {iaAcoes.map((a) => {
                const descartada = a.status === 'descartada';
                const aprovada = a.status === 'aprovada';
                const emAndamento = iaStatusId === a.id;
                return (
                  <Box
                    key={a.id}
                    sx={{
                      border: '1px solid',
                      borderColor: aprovada ? 'success.main' : 'divider',
                      borderRadius: 2,
                      p: 2,
                      opacity: descartada ? 0.55 : 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, alignItems: 'center' }}>
                      <Chip size="small" color={corDaPrioridade(a.priority)} label={ROTULO_PRIORIDADE[a.priority] ?? a.priority} />
                      <Chip size="small" variant="outlined" label={ROTULO_TIPO[a.actionType] ?? a.actionType} />
                      <Chip size="small" variant="outlined" label={a.setor} />
                      {a.dimensao && <Chip size="small" variant="outlined" label={a.dimensao} />}
                      <Chip size="small" variant="outlined" label={a.norma} />
                      {aprovada && <Chip size="small" color="success" label="Aprovada" />}
                      {descartada && <Chip size="small" label="Descartada" />}
                    </Box>
                    <Typography variant="body2" sx={{ mb: 0.5, textDecoration: descartada ? 'line-through' : 'none' }}>
                      {a.description}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Responsável sugerido: {ROTULO_RESPONSAVEL[a.responsible] ?? a.responsible}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {!aprovada && (
                          <Button size="small" color="success" onClick={() => handleStatusIA(a.id, 'aprovada')} disabled={emAndamento}>
                            Aprovar
                          </Button>
                        )}
                        {!descartada && (
                          <Button size="small" color="inherit" onClick={() => handleStatusIA(a.id, 'descartada')} disabled={emAndamento}>
                            Descartar
                          </Button>
                        )}
                        {(aprovada || descartada) && (
                          <Button size="small" onClick={() => handleStatusIA(a.id, 'sugerida')} disabled={emAndamento}>
                            Reabrir
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              <Divider />
              <Alert severity="success" icon={false} sx={{ '& b': { fontWeight: 700 } }}>
                <b>Como isto entra em ação:</b> as ações que você <b>aprovar</b> ficam salvas e viram,
                no próximo passo, itens do Plano de Ação — com prazo automático pela prioridade,
                responsável e status acompanhado (Planejada → Em andamento → Concluída).
              </Alert>
              <Typography variant="caption" color="text.secondary">
                {iaAcoes.filter((a) => a.status === 'aprovada').length} aprovadas ·{' '}
                {iaAcoes.length} sugestões salvas{iaModelo ? ` · modelo ${iaModelo}` : ''}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIaOpen(false)}>Fechar</Button>
          {!iaLoading && iaAcoes.length > 0 && (
            <Button variant="outlined" startIcon={<AIIcon />} onClick={handleGerarIA} disabled={iaGerando || criandoPlano}>
              Gerar novamente
            </Button>
          )}
          {!iaLoading && iaAcoes.some((a) => a.status === 'aprovada') && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCriarPlano}
              disabled={criandoPlano}
            >
              {criandoPlano ? 'Criando…' : 'Criar Plano de Ação com as aprovadas'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default PsychosocialCampaignDashboardPage;
